import { useState, useEffect } from 'react';
import { CreditCard, Lock, Shield, ArrowLeft, Mail, User, Eye, EyeOff, Loader } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import WaveAnimation from '../components/WaveAnimation';
import PaymentFailurePopup from '../components/common/PaymentFailurePopup';
import { register } from '../lib/auth';

interface FormErrors {
  [key: string]: string;
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showFailurePopup, setShowFailurePopup] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    cardNumber: '',
    expiryDate: '',
    cvc: '',
    nameOnCard: ''
  });

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      setIsLoggedIn(true);
      setFormData(prev => ({
        ...prev,
        name: userData.name || '',
        email: userData.email || ''
      }));
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const formatCardNumber = (value: string) => {
    return value.replace(/\s/g, '').match(/.{1,4}/g)?.join(' ') || '';
  };

  const formatExpiryDate = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!isLoggedIn) {
      if (!formData.name.trim()) {
        newErrors.name = 'Name is required';
      } else if (formData.name.trim().length < 2) {
        newErrors.name = 'Name must be at least 2 characters';
      }

      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }

      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    }

    if (!formData.cardNumber.replace(/\s/g, '')) {
      newErrors.cardNumber = 'Card number is required';
    } else if (formData.cardNumber.replace(/\s/g, '').length !== 16) {
      newErrors.cardNumber = 'Please enter a valid card number';
    }

    if (!formData.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    } else if (!/^\d{2}\/\d{2}$/.test(formData.expiryDate)) {
      newErrors.expiryDate = 'Please enter a valid expiry date (MM/YY)';
    }

    if (!formData.cvc) {
      newErrors.cvc = 'CVC is required';
    } else if (!/^\d{3,4}$/.test(formData.cvc)) {
      newErrors.cvc = 'Please enter a valid CVC';
    }

    if (!formData.nameOnCard.trim()) {
      newErrors.nameOnCard = 'Name on card is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (!isLoggedIn) {
        // Register the user
        const result = await register({
          name: formData.name,
          email: formData.email,
          password: formData.password
        });

        // Store auth data
        localStorage.setItem('auth_token', result.token);
        localStorage.setItem('user', JSON.stringify(result.user));
        localStorage.setItem('userId', result.user.id.toString());
      }

      // Simulate payment processing for 10 seconds
      await new Promise(resolve => setTimeout(resolve, 10000));
      
      // Show failure popup
      setShowFailurePopup(true);
      setLoading(false);
    } catch (err) {
      if (err instanceof Error) {
        try {
          const errorData = JSON.parse(err.message);
          const newErrors: FormErrors = {};
          
          errorData.forEach((error: any) => {
            if (error.path[0] === 'email') {
              newErrors.email = 'Please enter a valid email address';
            }
            if (error.path[0] === 'password') {
              newErrors.password = 'Password must be at least 6 characters long';
            }
            if (error.path[0] === 'name') {
              newErrors.name = 'Name must be at least 2 characters long';
            }
          });
          
          setErrors(newErrors);
        } catch {
          setErrors({ email: 'An error occurred during registration' });
        }
      } else {
        setErrors({ email: 'An unexpected error occurred' });
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 pt-24 relative overflow-hidden">
      <WaveAnimation />
      
      <div className="container mx-auto px-4 py-12 relative z-10">
        <button
          onClick={() => navigate('/pricing')}
          className="flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Pricing
        </button>

        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-8">
            <h1 className="text-3xl font-bold text-white mb-8">Complete Your Purchase</h1>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Account Information */}
              {!isLoggedIn ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                      <User className="w-5 h-5 text-purple-400" />
                      Account Information
                    </h2>
                    <Link 
                      to="/login" 
                      className="text-purple-400 hover:text-purple-300 text-sm"
                    >
                      Already have an account? Sign in
                    </Link>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-gray-700 border ${
                          errors.name ? 'border-red-500' : 'border-gray-600'
                        } rounded-lg focus:ring-2 focus:ring-purple-500 text-white`}
                        placeholder="Enter your full name"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`w-full pl-12 pr-4 py-3 bg-gray-700 border ${
                            errors.email ? 'border-red-500' : 'border-gray-600'
                          } rounded-lg focus:ring-2 focus:ring-purple-500 text-white`}
                          placeholder="Enter your email"
                        />
                      </div>
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className={`w-full pl-12 pr-12 py-3 bg-gray-700 border ${
                            errors.password ? 'border-red-500' : 'border-gray-600'
                          } rounded-lg focus:ring-2 focus:ring-purple-500 text-white`}
                          placeholder="Create a password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-purple-600/10 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="text-white font-medium">{user.name}</p>
                      <p className="text-gray-400 text-sm">{user.email}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Information */}
              <div>
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-purple-400" />
                  Payment Information
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-300 mb-2">
                      Card Number
                    </label>
                    <input
                      type="text"
                      id="cardNumber"
                      name="cardNumber"
                      value={formData.cardNumber}
                      onChange={(e) => {
                        const formatted = formatCardNumber(e.target.value);
                        setFormData(prev => ({ ...prev, cardNumber: formatted }));
                      }}
                      maxLength={19}
                      className={`w-full px-4 py-3 bg-gray-700 border ${
                        errors.cardNumber ? 'border-red-500' : 'border-gray-600'
                      } rounded-lg focus:ring-2 focus:ring-purple-500 text-white`}
                      placeholder="1234 5678 9012 3456"
                    />
                    {errors.cardNumber && (
                      <p className="mt-1 text-sm text-red-500">{errors.cardNumber}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="nameOnCard" className="block text-sm font-medium text-gray-300 mb-2">
                      Name on Card
                    </label>
                    <input
                      type="text"
                      id="nameOnCard"
                      name="nameOnCard"
                      value={formData.nameOnCard}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-gray-700 border ${
                        errors.nameOnCard ? 'border-red-500' : 'border-gray-600'
                      } rounded-lg focus:ring-2 focus:ring-purple-500 text-white`}
                      placeholder="Enter name on card"
                    />
                    {errors.nameOnCard && (
                      <p className="mt-1 text-sm text-red-500">{errors.nameOnCard}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-300 mb-2">
                        Expiry Date
                      </label>
                      <input
                        type="text"
                        id="expiryDate"
                        name="expiryDate"
                        value={formData.expiryDate}
                        onChange={(e) => {
                          const formatted = formatExpiryDate(e.target.value);
                          setFormData(prev => ({ ...prev, expiryDate: formatted }));
                        }}
                        maxLength={5}
                        className={`w-full px-4 py-3 bg-gray-700 border ${
                          errors.expiryDate ? 'border-red-500' : 'border-gray-600'
                        } rounded-lg focus:ring-2 focus:ring-purple-500 text-white`}
                        placeholder="MM/YY"
                      />
                      {errors.expiryDate && (
                        <p className="mt-1 text-sm text-red-500">{errors.expiryDate}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="cvc" className="block text-sm font-medium text-gray-300 mb-2">
                        CVC
                      </label>
                      <input
                        type="text"
                        id="cvc"
                        name="cvc"
                        value={formData.cvc}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          setFormData(prev => ({ ...prev, cvc: value }));
                        }}
                        maxLength={4}
                        className={`w-full px-4 py-3 bg-gray-700 border ${
                          errors.cvc ? 'border-red-500' : 'border-gray-600'
                        } rounded-lg focus:ring-2 focus:ring-purple-500 text-white`}
                        placeholder="123"
                      />
                      {errors.cvc && (
                        <p className="mt-1 text-sm text-red-500">{errors.cvc}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Security Note */}
              <div className="flex items-start gap-3 bg-purple-600/10 rounded-lg p-4">
                <Shield className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-purple-200">
                  Your payment information is encrypted and secure. We never store your full card details.
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 
                  disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors
                  flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Complete Purchase'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      <PaymentFailurePopup 
        isOpen={showFailurePopup}
        onClose={() => setShowFailurePopup(false)}
      />
    </div>
  );
}